/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useChatStore } from "@stores/chatStore";
import { ROUTES } from "@config/routes.config";
import { useAppSelector, useAppDispatch } from "@stores/redux/store";
import { ConversationSearch } from "./ConversationSearch";
import { ConversationFilters } from "./ConversationFilters";
import { ConversationItems } from "./ConversationItems";
import { fetchFacebookMessagesDetail, fetchZaloMessagesDetail } from "@features/messages/messagesSlice";
import { CHANNELS, PLATFORM_IDS } from "@common";
import type { Conversation } from "@interfaces/chat.interface";
import { ConversationTabHelpers } from "@constants/conversationTabs";
import { useConversationFetch } from "./hooks/useConversationFetch";
import { useConversationFolderStatusRefresh } from "./hooks/useConversationFolderStatusRefresh";
import { useConversationLoadMore } from "./hooks/useConversationLoadMore";
import { useConversationSearch } from "./hooks/useConversationSearch";
import { useConversationTagsAndAgents } from "./hooks/useConversationTagsAndAgents";
import styles from "./styles.module.less";

interface ConversationListProps {
  onOpenFanpageMenu?: () => void;
}

export const ConversationList = ({
  onOpenFanpageMenu,
}: ConversationListProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const activeTab = useChatStore((state) => state.activeTab);
  const setActiveTab = useChatStore((state) => state.setActiveTab);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    searchValue,
    setSearchValue,
    debouncedSearchValue,
    setDebouncedSearchValue,
    handleSearch,
    handleChange,
    searchDebounceRef,
  } = useConversationSearch();

  const { pages: facebookPages, loading: fanpagesLoading } = useAppSelector(
    (state) => state.fanpage
  );
  const { oas: zaloOAs } = useAppSelector((state) => state.zalo);

  const agentsByConversation = useAppSelector(
    (state) => state.agents.agentsByConversation
  );

  const selectedFanpageName = useChatStore(
    (state) => state.selectedFanpageName
  );
  const selectedChannel = useChatStore((state) => state.selectedChannel);
  
  const setConversationsWithSearch = useChatStore(
    (state) => state.setConversationsWithSearch
  );
  const updatePagination = useCallback(
    (pageId: string, pagination: { offset: number; total: number; limit: number; hasMore: boolean }) => {
      useChatStore.setState({
        conversationPagination: {
          ...useChatStore.getState().conversationPagination,
          [pageId]: pagination,
        },
      });
    },
    []
  );
  
  const prevFanpageRef = useRef<string | undefined>(selectedFanpageName);
  const isResettingPageRef = useRef(false);
  const updateConversationTagsMap = useChatStore((state) => state.updateConversationTagsMap);
  
  useEffect(() => {
    if (prevFanpageRef.current && prevFanpageRef.current !== selectedFanpageName) {
      isResettingPageRef.current = true;
      setActiveTab("ALL");
      setSearchValue("");
      setDebouncedSearchValue("");
      setSelectedTagIds([]);
      updateConversationTagsMap({});
      setConversationFolderStatusMap(null);
      const { conversations: currentConversations } = useChatStore.getState();
      const oldFanpageName = prevFanpageRef.current;
      const conversationsFromOtherFanpages = currentConversations.filter(
        (conv) => conv.fanpageName !== oldFanpageName
      );
      setConversationsWithSearch(conversationsFromOtherFanpages, "");
      isResettingPageRef.current = false;
    }
    prevFanpageRef.current = selectedFanpageName;
  }, [selectedFanpageName, setConversationsWithSearch, updateConversationTagsMap]);

  const displayConversations = useChatStore(
    (state) => state.filteredConversations
  );

  const conversationPagination = useChatStore(
    (state) => state.conversationPagination
  );

  const appendConversations = useChatStore(
    (state) => state.appendConversations
  );

  useEffect(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    if (
      selectedFanpageName &&
      displayConversations.length === 0 &&
      !debouncedSearchValue
    ) {
      setIsLoadingConversations(true);
      setShowEmptyState(false);

      loadingTimeoutRef.current = setTimeout(() => {
        setIsLoadingConversations(false);
        setShowEmptyState(true);
      }, 800);
    } else if (displayConversations.length > 0) {
      setIsLoadingConversations(false);
      setShowEmptyState(false);
    } else {
      setIsLoadingConversations(false);
      setShowEmptyState(displayConversations.length === 0);
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [selectedFanpageName, displayConversations.length, debouncedSearchValue]);

  const selectedConversationId = useChatStore(
    (state) => state.selectedConversationId
  );
  const selectConversation = useChatStore((state) => state.selectConversation);
  const markConversationAsRead = useChatStore(
    (state) => state.markConversationAsRead
  );
  const togglePin = useChatStore((state) => state.togglePin);
  const toggleMute = useChatStore((state) => state.toggleMute);
  const isSyncing = useChatStore((state) => state.isSyncing);
  const selectedPageId = useChatStore((state) => state.selectedPageId);
  
  // Use selectedPageId for unique identification instead of name
  const selectedPage = selectedPageId
    ? facebookPages.find((page) => page.id === selectedPageId)
    : null;
  const isCurrentPageSyncing = isSyncing && selectedPage?.messageSyncStatus === "IN_PROGRESS";
  const updateConversationFolderStatus = useChatStore(
    (state) => state.updateConversationFolderStatus
  );
  const storeFolderStatusMap = useChatStore(
    (state) => state.conversationFolderStatusMap
  );
  const conversationTagsMap = useChatStore((state) => state.conversationTagsMap);
  
  // Use selectedPageId for Zalo OA as well
  const selectedOA = selectedPageId
    ? zaloOAs.find((oa) => oa.id === selectedPageId)
    : null;

  const platformFromChannel = useCallback((channel: string) => {
    if (channel === CHANNELS.FACEBOOK) return PLATFORM_IDS.FACEBOOK;
    return channel;
  }, []);

  const handleSelectConversation = (conversation: Conversation) => {
    const platform = platformFromChannel(conversation.channel);
    const accountId =
      conversation.channel === CHANNELS.FACEBOOK
        ? selectedPage?.id
        : conversation.channel === CHANNELS.ZALO
        ? selectedOA?.id
        : undefined;
    selectConversation(conversation.id);
    navigate(
      ROUTES.CONVERSATION(conversation.id, platform, accountId),
      { replace: true }
    );
    if (conversation.unreadCount > 0) {
      markConversationAsRead(conversation.id);
    }

    // Handle Facebook messages
    if (conversation.channel === CHANNELS.FACEBOOK && selectedFanpageName) {
      const selectedPage = facebookPages.find(
        (page) => page.name === selectedFanpageName
      );

      if (selectedPage) {
        dispatch(
          fetchFacebookMessagesDetail({
            conversationId: conversation.id,
            pageId: selectedPage.id,
            offset: 0,
            limit: 100,
          })
        );
      }
    }

    // Handle Zalo messages
    if (conversation.channel === CHANNELS.ZALO && selectedFanpageName) {
      dispatch(
        fetchZaloMessagesDetail({
          conversationId: conversation.id,
          offset: 0,
          limit: 100,
        })
      );
    }
  };

  const { isLoadingMore, hasMore, loadMoreSentinelRef } = useConversationLoadMore({
    selectedFanpageName,
    selectedChannel,
    debouncedSearchValue,
    facebookPages,
    zaloOAs,
    conversationPagination,
    appendConversations,
    activeTab,
    updateConversationTagsMap,
    displayConversationsLength: displayConversations.length,
    scrollContainerRef,
  });

  const [conversationFolderStatusMap, setConversationFolderStatusMap] = useState<{
    items: Array<{ folder?: string; externalId: string }>;
  } | null>(null);

  const storeTrackedIdsRef = useRef<Set<string>>(new Set());
  const isFetchingFromFolderStatusChangeRef = useRef(false);
  useConversationTagsAndAgents({
    displayConversations,
    selectedFanpageName,
    facebookPages,
    activeTab,
    updateConversationTagsMap,
    dispatch,
  });

  useConversationFetch({
    selectedTagIds,
    selectedFanpageName,
    selectedChannel,
    selectedPageId,
    facebookPages,
    zaloOAs,
    debouncedSearchValue,
    activeTab,
    setConversationsWithSearch,
    setIsLoadingConversations,
    setConversationFolderStatusMap,
    updateConversationFolderStatus,
    selectConversation,
    isResettingPageRef,
    isFetchingFromFolderStatusChangeRef,
    searchDebounceRef,
    updatePagination,
  });

  // Filter conversations theo activeTab và folder status

  useConversationFolderStatusRefresh({
    storeFolderStatusMap,
    selectedFanpageName,
    facebookPages,
    debouncedSearchValue,
    activeTab,
    selectedTagIds,
    setConversationsWithSearch,
    isResettingPageRef,
    isFetchingFromFolderStatusChangeRef,
    updatePagination,
  });

  useEffect(() => {
    Object.keys(storeFolderStatusMap).forEach((externalId) => {
      storeTrackedIdsRef.current.add(externalId);
    });
  }, [storeFolderStatusMap]);

  const mergedFolderStatusMap = useMemo(() => {
    const localItems = conversationFolderStatusMap?.items || [];
    const mergedMap = new Map<string, { externalId: string; folder?: string }>();
    Object.entries(storeFolderStatusMap).forEach(([externalId, folder]) => {
      if (folder && folder.trim() !== "") {
        mergedMap.set(externalId, { externalId, folder });
      }
    });
    
    localItems.forEach((item) => {
      if (!mergedMap.has(item.externalId) && item.folder) {
        const wasTrackedInStore = storeTrackedIdsRef.current.has(item.externalId);
        
        if (!wasTrackedInStore) {
          mergedMap.set(item.externalId, item);
        }
      }
    });

    return Array.from(mergedMap.values());
  }, [conversationFolderStatusMap, storeFolderStatusMap]);
  // Filter conversations by activeTab and folder status
  const filteredConversations = useMemo(() => {
    if (!ConversationTabHelpers.requiresFolderStatus(activeTab)) {
      return displayConversations;
    }
    // Skip folderStatus filtering for ESCALATED tab (already filtered by API)
    if (activeTab === "ESCALATED") {
      return displayConversations;
    }

    const folderStatusMap = new Map(
      mergedFolderStatusMap.map((item) => [item.externalId, item.folder || ""])
    );

    const expectedFolder = ConversationTabHelpers.getFolderForTab(activeTab);

    const filtered = displayConversations.filter((conv) => {
      const conversationId = conv.lastMessage?.conversationId || conv.id;
      const folder = folderStatusMap.get(conversationId) || "";

      if (expectedFolder) {
        return folder === expectedFolder;
      }

      return true;
    });

    return filtered;
  }, [displayConversations, activeTab, mergedFolderStatusMap]);

  // Track prev filtered conversations to detect new conversations
  const prevFilteredConversationsRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    const currentIds = new Set(filteredConversations.map((conv) => conv.id));
    prevFilteredConversationsRef.current = currentIds;
  }, [filteredConversations, activeTab, selectedConversationId, selectedFanpageName, selectedPage, selectedOA, selectConversation, navigate, dispatch, platformFromChannel]);

  const allConversations = useChatStore((state) => state.conversations);
  const deselectedConversationIdsRef = useRef<Set<string>>(new Set());
  const isDeselectingRef = useRef<boolean>(false);
  const movedConversationInfo = useChatStore((state) => state.movedConversationInfo);
  const prevActiveTabRef = useRef<typeof activeTab>(activeTab);

  // Deselect conversation when tab changes if it's not in the new tab's filtered list
  useEffect(() => {
    if (prevActiveTabRef.current !== activeTab) {
      // Clear deselected set to allow deselection check
      deselectedConversationIdsRef.current.clear();
      
      // If there's a selected conversation, check if it should be deselected
      if (selectedConversationId && !isLoadingConversations) {
        const isActiveConversationInFiltered = filteredConversations.some(
          (conv) => conv.id === selectedConversationId
        );
        
        // Deselect if conversation is not in the new tab's filtered list
        if (!isActiveConversationInFiltered) {
          isDeselectingRef.current = true;
          deselectedConversationIdsRef.current.add(selectedConversationId);
          selectConversation(undefined);
          setTimeout(() => {
            isDeselectingRef.current = false;
          }, 0);
        }
      }
      
      prevActiveTabRef.current = activeTab;
    }
  }, [activeTab, selectedConversationId, filteredConversations, isLoadingConversations, selectConversation]);

  useEffect(() => {
    // If currently in deselect process, do nothing
    if (isDeselectingRef.current) {
      return;
    }

    // Reset deselected set when selectedConversationId changes (user selects new conversation)
    if (selectedConversationId) {
      const prevIds = prevFilteredConversationsRef.current;
      // Only clear if new conversation is different from previous conversation
      if (!prevIds.has(selectedConversationId)) {
        deselectedConversationIdsRef.current.clear();
      }
    }

    if (
      selectedConversationId &&
      !isLoadingConversations &&
      !deselectedConversationIdsRef.current.has(selectedConversationId)
    ) {
      const isActiveConversationInFiltered = filteredConversations.some(
        (conv) => conv.id === selectedConversationId
      );
      const isActiveConversationInAll = allConversations.length > 0 && allConversations.some(
        (conv) => conv.id === selectedConversationId
      );

      // Deselect if conversation is no longer in filtered list
      // This handles cases where conversation state changes (e.g., folder status changes)
      if (!isActiveConversationInFiltered) {
        // Get conversation folder status to determine which tab it moved to
        isDeselectingRef.current = true;
        deselectedConversationIdsRef.current.add(selectedConversationId);
        selectConversation(undefined);
        // Reset flag after deselect
        setTimeout(() => {
          isDeselectingRef.current = false;
        }, 0);
      } else if (allConversations.length > 0 && !isActiveConversationInAll) {
        // If conversation is not in any conversations at all, also deselect
        isDeselectingRef.current = true;
        deselectedConversationIdsRef.current.add(selectedConversationId);
        selectConversation(undefined);
        setTimeout(() => {
          isDeselectingRef.current = false;
        }, 0);
      }
    }
  }, [
    filteredConversations,
    selectedConversationId,
    selectConversation,
    allConversations,
    isLoadingConversations,
    activeTab,
  ]);

  // Removed auto-select logic - conversations should only be selected when user clicks on them
  // This prevents auto-loading messages when new conversations arrive from socket

  return (
    <div className={styles.conversationList}>
      <ConversationSearch
        searchValue={searchValue}
        onSearch={handleSearch}
        onChange={handleChange}
        selectedTagIds={selectedTagIds}
        onTagsChange={setSelectedTagIds}
      />
      <ConversationFilters
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <ConversationItems
        conversations={filteredConversations}
        isLoading={isLoadingConversations || fanpagesLoading}
        isLoadingMore={isLoadingMore}
        showEmptyState={showEmptyState}
        searchValue={searchValue}
        selectedFanpageName={selectedFanpageName}
        selectedConversationId={selectedConversationId}
        hasMore={hasMore}
        conversationTagsMap={conversationTagsMap}
        conversationFolderStatusMap={mergedFolderStatusMap}
        agentsByConversation={agentsByConversation}
        onSelectConversation={handleSelectConversation}
        movedConversationInfo={movedConversationInfo}
        onPin={(conversationId) => {
          const pageId = selectedPage?.id;
          if (pageId) {
            togglePin(conversationId, pageId);
          } else {
            togglePin(conversationId);
          }
        }}
        onMute={toggleMute}
        onDelete={(conversationId) => {
          console.log("Xóa hội thoại", conversationId);
        }}
        onOpenFanpageMenu={onOpenFanpageMenu}
        scrollContainerRef={scrollContainerRef}
        loadMoreSentinelRef={loadMoreSentinelRef}
        isSyncing={isCurrentPageSyncing}
      />
    </div>
  );
};
